import { useEffect, useMemo, useState } from 'react';
import { LineChart } from 'lucide-react';
import { TransferCurveChart } from './TransferCurveChart';
import { transferCurveData, penaltyCurveData, type OrganScore } from './organHealth';
import { fetchMethodCurves, type MethodCurves } from '../../api/organHealthApi';

/**
 * "How each test maps to its score" — the methodology's core idea, per organ.
 * One small transfer curve per transfer sub-score, with the patient's real
 * point plotted on the real function (e.g. eGFR 38 → 51). The curve anchors
 * come from the scorer's own config (single source of truth) and the point is
 * the persisted raw value + real sub-score, so the dot sits exactly on the
 * line — the visual proof of how the number was reached.
 *
 * Self-contained and non-blocking: fetches the (module-cached) curves once,
 * renders nothing until they load or when a score has no transfer inputs (older
 * scores, the Overall roll-up, penalty-only params). It never blocks the score.
 */
export function TransferCurvePanel({ organ }: { organ: OrganScore }) {
  const [curves, setCurves] = useState<MethodCurves | null>(null);

  useEffect(() => {
    let live = true;
    void fetchMethodCurves().then((c) => {
      if (live) setCurves(c);
    });
    return () => {
      live = false;
    };
  }, []);

  const data = useMemo(() => (curves ? transferCurveData(organ, curves.transfers) : []), [organ, curves]);
  const penalties = useMemo(
    () => (curves ? penaltyCurveData(organ, curves.penalties) : []),
    [organ, curves],
  );
  if (data.length === 0 && penalties.length === 0) return null;

  return (
    <div className="rounded border border-border-subtle bg-surface-sunken/30 p-4">
      <div className="flex items-center gap-2">
        <LineChart className="size-4 text-text-tertiary" aria-hidden="true" />
        <span className="text-xs font-medium uppercase tracking-[0.08em] text-text-tertiary">
          How each test maps to its score
        </span>
      </div>

      {data.length > 0 && (
        <div className="mt-3 grid gap-x-5 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((d) => (
            <figure key={d.key} className="min-w-0">
              <figcaption className="mb-1 flex items-baseline justify-between gap-2">
                <span className="text-[12px] font-medium text-text-primary">{d.label}</span>
                {d.patient ? (
                  <span className="text-[12px] tabular-nums text-text-tertiary">
                    {d.patient.x} <span className="text-text-tertiary/70">→</span>{' '}
                    <span className="font-medium text-text-primary">{d.patient.y}</span>
                  </span>
                ) : (
                  <span className="text-[12px] tabular-nums text-text-tertiary">
                    score <span className="font-medium text-text-primary">{d.subScore}</span>
                  </span>
                )}
              </figcaption>
              <TransferCurveChart curve={d.anchors} anchors={d.anchors} patientValue={d.patient} xLabel={d.xLabel} />
              {!d.patient ? (
                <p className="mt-1 text-[11px] leading-snug text-text-tertiary">
                  Recorded value not stored with this score — Rescore to plot the patient's point.
                </p>
              ) : null}
            </figure>
          ))}
        </div>
      )}

      {penalties.length > 0 && (
        <div className="mt-4 border-t border-border-subtle pt-3">
          <div className="text-[11px] text-text-tertiary">
            Penalties — a bounded amount subtracted from the base score
          </div>
          <div className="mt-2 grid gap-x-5 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
            {penalties.map((d) => (
              <figure key={d.key} className="min-w-0">
                <figcaption className="mb-1 flex items-baseline justify-between gap-2">
                  <span className="text-[12px] font-medium text-text-primary">{d.label}</span>
                  <span className="text-[12px] tabular-nums text-text-tertiary">
                    {d.patient.x} <span className="text-text-tertiary/70">→</span>{' '}
                    <span className="font-medium text-band-severe-text">−{d.patient.y}</span>
                  </span>
                </figcaption>
                <TransferCurveChart
                  curve={d.curve}
                  patientValue={d.patient}
                  xLabel={d.xLabel}
                  xScale={d.scale}
                  xDomain={d.xDomain}
                  xTicks={d.xTicks}
                  yDomain={[0, Math.ceil(d.yMax)]}
                  yTicks={[0, Math.round(d.yMax / 2), Math.ceil(d.yMax)]}
                />
              </figure>
            ))}
          </div>
        </div>
      )}

      <p className="mt-2 text-[11px] leading-snug text-text-tertiary">
        Solid line: the curve the score reads from. Dashed (transfers): the rubric&apos;s flat stage steps, shown for
        contrast. The dot is this patient&apos;s value on the curve.
      </p>
    </div>
  );
}
